import { format, parseISO } from 'date-fns';

// Counts from 1 to 4 arrive as null (privacy suppression). Never try to
// work them back out from totals. A field that is missing altogether is
// not a suppressed count, so it shows as a dash.
export const SUPPRESSED_LABEL = '< 5';
export const SUPPRESSED_HINT =
  'Fewer than 5 learners; hidden to protect privacy';

export const formatCount = (value) => {
  if (value === undefined) return '–';
  if (value === null) return SUPPRESSED_LABEL;
  return value.toLocaleString();
};

export const formatPercent = (value) =>
  value === null || value === undefined ? '–' : `${value.toFixed(1)}%`;

// Share as a percentage with one decimal, only when both counts are real
// numbers: a suppressed (null) count is never worked back from a rate.
export const rate = (part, whole) =>
  typeof part === 'number' && typeof whole === 'number' && whole > 0
    ? Math.round((part / whole) * 1000) / 10
    : null;

// Graduation rate from a level's funnel: graduates as a share of enrolled in
// ended cohorts with progress data. The headline and the tables both use it,
// so the same year or cohort always shows the same rate.
export const funnelGraduationRate = (level) => {
  const stage = (level?.funnel?.stages ?? []).find(
    (s) => s.stage === 'graduated'
  );
  return (level?.funnel?.cohorts ?? 0) > 0
    ? stage?.percent_of_enrolled ?? null
    : null;
};

// A level still running (no ended cohort yet) shows "In progress" when it
// has no funnel; otherwise a missing rate is a dash.
export const levelStillRunning = (level) =>
  level?.status ? level.status !== 'ended' : (level?.cohorts?.ended ?? 0) === 0;

export const formatGraduationRate = (level) => {
  if ((level?.funnel?.cohorts ?? 0) === 0) {
    return levelStillRunning(level) ? 'In progress' : '–';
  }
  return formatPercent(funnelGraduationRate(level));
};

export const cohortCountLabel = (cohorts) => {
  const total = cohorts?.total ?? 0;
  const live = cohorts?.live ?? 0;
  return `${total} cohort${total === 1 ? '' : 's'}${
    live ? ` · ${live} live` : ''
  }`;
};

// "Active" needs progress data. Cohorts without any are listed by name in
// summary.notes.cohorts_without_progress_data; for those, null means "No data
// yet", not "< 5". (funnel.cohorts can't tell: live cohorts always have 0.)
export const noProgressNames = (summary) =>
  new Set(summary?.notes?.cohorts_without_progress_data ?? []);

// A cohort has data unless it is listed; a year (or all time, given every
// year's cohorts) has data if any non-upcoming cohort does.
export const hasProgressData = (level, withoutProgress) => {
  // Upcoming cohorts have no progress yet and aren't in the notes list.
  if (level?.status) {
    return level.status !== 'upcoming' && !withoutProgress.has(level.name);
  }
  const cohorts = level?.cohort_list;
  if (!cohorts) return true;
  return cohorts.some(
    (cohort) =>
      cohort.status !== 'upcoming' && !withoutProgress.has(cohort.name)
  );
};

// Takes the level so a cohort with no learners yet (not in the notes list
// either) shows 0 instead of a suppressed "< 5".
export const formatActive = (level, hasData) => {
  if (level?.enrolled === 0) return '0';
  if (!hasData && level?.active === null) return 'No data yet';
  return formatCount(level?.active);
};

export const formatDate = (date) =>
  date ? format(parseISO(date), 'd MMM yyyy') : '';

export const humanize = (value) =>
  value === 'non_binary'
    ? 'Non-binary'
    : value.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
